import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Shield, Unlock } from "lucide-react";
import { toast } from "sonner";
import { formatMinutes, formatDecimalHours, formatWeekLabel } from "@/lib/timeFormat";
import StatusPill from "@/components/hours/StatusPill";
import StatusFilter from "@/components/hours/StatusFilter";

export default function AdminApprovalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", period: "" });
  const [override, setOverride] = useState(null); // { ts, action }
  const [unlock, setUnlock] = useState(null); // { ts }
  const [reason, setReason] = useState("");
  // Phase 30 iter-5: admin picks whether unlock returns the sheet to
  // `submitted` (admin can re-approve directly) or `draft` (worker
  // must re-submit — use when data needs worker correction).
  const [unlockTarget, setUnlockTarget] = useState("submitted");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.status && filters.status !== "all") p.set("status", filters.status);
      if (filters.period) p.set("period", filters.period);
      const { data } = await api.get(`/tenant/approvals/all?${p.toString()}`);
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doOverride = async () => {
    if (!override) return;
    if (override.action === "reject" && !reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/tenant/approvals/${override.ts.id}/override`, {
        action: override.action, reason: reason || undefined,
      });
      toast.success(`Override ${override.action} applied`);
      setOverride(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doUnlock = async () => {
    if (!unlock) return;
    setBusy(true);
    try {
      await api.post(`/tenant/timesheets/${unlock.ts.id}/unlock`, {
        reason: reason || undefined,
        target_state: unlockTarget,
      });
      toast.success(
        unlockTarget === "submitted"
          ? "Unlocked to submitted; you can re-approve"
          : "Unlocked to draft; worker must re-submit",
      );
      setUnlock(null); setReason(""); setUnlockTarget("submitted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-approvals-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Approvals oversight</h1>
        <p className="mt-2 text-muted-foreground">All timesheets across this workspace. Override or unlock as needed — every action is audited.</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="flex flex-col gap-1">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Status</Label>
            <StatusFilter
              value={filters.status === "all" ? null : filters.status}
              onChange={(v) => setFilters({ ...filters, status: v || "all" })}
              persistKey="hours-filter:admin-approvals"
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Period start (Sat)</Label>
            <Input type="date" value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })} data-testid="admin-filter-period" />
          </div>
          <Button onClick={load} data-testid="admin-filter-apply">Apply</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Timesheets ({items.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Worker</TableHead>
                <TableHead>Approver</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="py-8 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No timesheets match.</TableCell></TableRow>}
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`admin-ts-${it.id}`}>
                  <TableCell className="font-medium">
                    <Link to={`/app/approvals/${it.id}`} className="hover:underline">{it.worker_name}</Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{it.approver_name || "—"}</TableCell>
                  <TableCell>{formatWeekLabel(it.period_start_local, it.period_end_local)}</TableCell>
                  <TableCell className="tabular-nums">{formatMinutes(it.total_minutes)} ({formatDecimalHours(it.total_minutes)}h)</TableCell>
                  <TableCell>
                    <StatusPill
                      status={it.status === "revised" ? "submitted" : it.status}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell><Badge variant="outline" className="rounded-full text-[10px] uppercase">{it.submission_source || "worker"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {it.status === "submitted" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => { setOverride({ ts: it, action: "approve" }); setReason(""); }} data-testid={`override-approve-${it.id}`}>
                            <Shield className="h-4 w-4" /> Approve
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setOverride({ ts: it, action: "reject" }); setReason(""); }} data-testid={`override-reject-${it.id}`}>
                            <Shield className="h-4 w-4" /> Reject
                          </Button>
                        </>
                      )}
                      {(it.status === "approved" || it.status === "submitted" || it.status === "rejected") && (
                        <Button size="sm" variant="ghost" onClick={() => { setUnlock({ ts: it }); setReason(""); }} data-testid={`unlock-${it.id}`}>
                          <Unlock className="h-4 w-4" /> Unlock
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!override} onOpenChange={(v) => !v && setOverride(null)}>
        <DialogContent data-testid="override-dialog">
          <DialogHeader><DialogTitle>Admin override: {override?.action}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Reason {override?.action === "reject" && "(required)"}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} data-testid="override-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverride(null)}>Cancel</Button>
            <Button onClick={doOverride} disabled={busy || (override?.action === "reject" && !reason.trim())} data-testid="override-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!unlock} onOpenChange={(v) => !v && (setUnlock(null) || setUnlockTarget("submitted"))}>
        <DialogContent data-testid="unlock-dialog">
          <DialogHeader><DialogTitle>Unlock timesheet</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Pick what happens after unlock. Default: return to <span className="font-medium">submitted</span> so you can
            re-approve immediately. Pick <span className="font-medium">draft</span> only when the worker needs to fix
            data and re-submit.
          </p>
          <div className="space-y-3">
            <div className="flex flex-col gap-1">
              <Label>After unlock</Label>
              <Select value={unlockTarget} onValueChange={setUnlockTarget}>
                <SelectTrigger data-testid="unlock-target-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="submitted" data-testid="unlock-target-submitted">
                    Return to submitted (admin can re-approve)
                  </SelectItem>
                  <SelectItem value="draft" data-testid="unlock-target-draft">
                    Return to worker as draft (requires re-submit)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Label>Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="unlock-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUnlock(null); setUnlockTarget("submitted"); }}>Cancel</Button>
            <Button onClick={doUnlock} disabled={busy} data-testid="unlock-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
